import { logger } from '@adornis/base/logging.js';
import type { Constructor, Maybe } from '@adornis/base/utilTypes.js';
import { ID, Int } from '@adornis/baseql/baseqlTypes.js';
import type { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import { baseqlMetaData } from '@adornis/baseql/metadata/metaDataStore.js';
import { registerMutation, registerQuery, registerSubscription } from '@adornis/baseql/metadata/register.js';
import { context } from '@adornis/baseql/server/context.js';
import { watchEntireCollection } from '@adornis/baseql/server/watchQuery.js';
import { type BaseQLSelectionSet, selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { CustomMailRequest } from '@adornis/bh-mails/custom-mail-request.js';
import { checkAuthorizedMiddleware } from '@adornis/bh-util/middlewares/checkAuthorizedMiddleware.js';
import {
  readEntitiesByIDs,
  watchEntitiesByIDs,
  readAllEntities,
  watchAllEntities,
} from '@adornis/bh-util/server/entity-reads.js';
import { delay } from '@adornis/bh-util/util.js';
import { mailer } from '@adornis/buchungsheld-db/mails/communication.js';
import { mongo } from '@adornis/buchungsheld-db/server/mongo.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import type { IntAspect } from '@adornis/filter/aspect-schemas/IntAspect.js';
import { Recipient } from '@adornis/mails/db/recipients.js';
import { prepareMessage } from '@adornis/mails/server/communication.js';
import { getMyself } from '@adornis/users/db/api/currentUserInfo.operations.js';
import { AdornisRoleToContextsWrapper } from '@adornis/users/db/roleToContextsWrapper.js';
import { concatMap, from, map, merge, type Observable, of, switchMap } from 'rxjs';
import { BuddyUser } from '../entities/buddy-user.js';
import { MatchingPermission } from '../permissions.js';
import { getMenteeUserClass } from '../user-class.js';

function resolveBuddyUserReadClass(userClass: string): typeof MongoEntity & Constructor<BuddyUser> {
  const node = baseqlMetaData.entities.resolveNode(userClass);
  const entity = node.type();
  if (typeof entity !== 'function' || !(entity.prototype instanceof BuddyUser))
    throw new Error(`Entity ${userClass} is not a registered BuddyUser subclass`);
  return entity as typeof MongoEntity & Constructor<BuddyUser>;
}

export const getBuddyUsersByIDs = registerQuery({
  operationName: 'getBuddyUsersByIDs',
  type: () => [BuddyUser],
  params: [
    { name: 'userClass', type: () => String },
    { name: 'ids', type: () => [ID] },
  ],
  resolve:
    (userClass: string, ids: string[]) =>
    <SelSet extends BaseQLSelectionSet<BuddyUser>>(gqlFields: SelSet) =>
      readEntitiesByIDs(resolveBuddyUserReadClass(userClass), ids, gqlFields),
});

export const subscribeBuddyUsersByIDs = registerSubscription({
  operationName: 'subscribeBuddyUsersByIDs',
  type: () => [BuddyUser],
  params: [
    { name: 'userClass', type: () => String },
    { name: 'ids', type: () => [ID] },
  ],
  resolve:
    (userClass: string, ids: string[]) =>
    <SelSet extends BaseQLSelectionSet<BuddyUser>>(gqlFields: SelSet) =>
      watchEntitiesByIDs(resolveBuddyUserReadClass(userClass), ids, gqlFields),
});

export const getAllBuddyUsers = registerQuery({
  operationName: 'getAllBuddyUsers',
  type: () => [BuddyUser],
  params: [
    { name: 'userClass', type: () => String },
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'excludeIDs', type: () => [ID] },
  ],
  resolve:
    (userClass: string, skip?: number, limit?: number, excludeIDs?: string[]) =>
    <SelSet extends BaseQLSelectionSet<BuddyUser>>(gqlFields: SelSet) =>
      readAllEntities(resolveBuddyUserReadClass(userClass), gqlFields, { skip, limit, excludeIDs }),
});

export const subscribeAllBuddyUsers = registerSubscription({
  operationName: 'subscribeAllBuddyUsers',
  type: () => [BuddyUser],
  params: [
    { name: 'userClass', type: () => String },
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'excludeIDs', type: () => [ID] },
  ],
  resolve:
    (userClass: string, skip?: number, limit?: number, excludeIDs?: string[]) =>
    <SelSet extends BaseQLSelectionSet<BuddyUser>>(gqlFields: SelSet) =>
      watchAllEntities(resolveBuddyUserReadClass(userClass), gqlFields, { skip, limit, excludeIDs }),
});

export const getBuddyUserByID =
  (userClass: string, id: string | null | undefined) =>
  async <SelSet extends BaseQLSelectionSet<BuddyUser>>(gqlFields: SelSet) => {
    if (!id) return null;
    return (await getBuddyUsersByIDs(userClass, [id])(gqlFields))[0] ?? null;
  };

export const subscribeBuddyUserByID =
  (userClass: string, id: string | null | undefined) =>
  <SelSet extends BaseQLSelectionSet<BuddyUser>>(gqlFields: SelSet) =>
    id ? subscribeBuddyUsersByIDs(userClass, [id])(gqlFields).pipe(map(entities => entities[0] ?? null)) : of(null);

function getNumMatchesFilter(numMatchesAspect?: IntAspect | null) {
  if (!numMatchesAspect) return [];
  if (numMatchesAspect.mode === 'equals') return [{ $match: { numMatches: { $eq: numMatchesAspect.min } } }];
  if (numMatchesAspect.mode === 'isHigher') return [{ $match: { numMatches: { $gte: numMatchesAspect.min } } }];
  if (numMatchesAspect.mode === 'isLower') return [{ $match: { numMatches: { $lte: numMatchesAspect.min } } }];
  if (numMatchesAspect.mode === 'range')
    return [{ $match: { numMatches: { $gte: numMatchesAspect.min ?? 0, $lte: numMatchesAspect.max ?? Infinity } } }];

  throw new Error('unknown mode: ' + numMatchesAspect.mode);
}

const getInTimeFrameCount = async (
  userClass: string,
  timeframeID: string,
  onlyUnmatched: boolean,
  filter?: AdornisFilter,
) => {
  const collection = mongo().collection<BuddyUser>(BuddyUser._collectionName);
  const isMenteeClass = userClass === getMenteeUserClass()._class;
  const numMatchesAspect = filter?.aspects?.find(aspect => aspect.customID === 'numMatches') as IntAspect | null;

  const result = (
    await collection
      .aggregate([
        { $match: { _class: userClass, timeframeID } },
        {
          $lookup: {
            from: 'match',
            as: 'matches',
            localField: '_id',
            foreignField: isMenteeClass ? 'menteeID' : 'mentorID',
          },
        },
        ...(onlyUnmatched ? [{ $match: { matches: [] } }] : []),
        ...(filter ? [{ $match: filter.getMongoFilter() }] : []),
        ...(numMatchesAspect ? [{ $addFields: { numMatches: { $size: '$matches' } } }] : []),
        ...getNumMatchesFilter(numMatchesAspect),
      ])
      .toArray()
  ).length;

  return result;
};

const subscribeAllBuddyInCurrentTimeframeResolver = (
  userClass: string,
  timeframeID: string,
  filter: AdornisFilter,
  onlyUnmatched: boolean,
  skip?: number,
  limit?: number,
) => {
  const query = { _class: userClass, timeframeID };
  return watchEntireCollection(BuddyUser._collectionName).pipe(
    switchMap(async () => {
      const numMatchesAspect = filter.aspects?.find(aspect => aspect.customID === 'numMatches') as IntAspect | null;
      const isMenteeClass = userClass === getMenteeUserClass()._class;

      const aggregation = [
        { $match: query },
        {
          $lookup: {
            from: 'match',
            as: 'matches',
            localField: '_id',
            foreignField: isMenteeClass ? 'menteeID' : 'mentorID',
          },
        },
        ...(onlyUnmatched ? [{ $match: { matches: [] } }] : []),
        { $addFields: { numMatches: { $size: '$matches' } } },
        { $match: filter.getMongoFilter() },
        { $sort: filter.getMongoOptions().sort },
        ...getNumMatchesFilter(numMatchesAspect),
        ...(isMenteeClass
          ? [
              {
                $lookup: {
                  from: BuddyUser._collectionName,
                  as: 'preferedMentors',
                  localField: 'preferedMentorsIDs',
                  foreignField: '_id',
                },
              },
            ]
          : []),
        ...(skip ? [{ $skip: skip }] : []),
        ...(limit ? [{ $limit: limit }] : []),
      ];

      const result = await mongo()
        .collection<EntityData<BuddyUser>>(BuddyUser._collectionName)
        .aggregate(aggregation, { collation: { locale: 'en', strength: 1 } })
        .toArray();
      return result as unknown as BuddyUser[];
    }),
  ) as Observable<BuddyUser[]>;
};

export const getAllBuddiesInCurrentTimeframeResolver = async (
  userClass: string,
  timeframeID: string,
  filter: AdornisFilter,
  onlyUnmatched: boolean,
  skip?: number,
  limit?: number,
) => {
  const collection = mongo().collection<BuddyUser>(BuddyUser._collectionName);
  const isMenteeClass = userClass === getMenteeUserClass()._class;
  const query = { _class: userClass, timeframeID };
  const numMatchesAspect = filter.aspects?.find(aspect => aspect.customID === 'numMatches') as IntAspect | null;
  return await collection
    .aggregate([
      { $match: query },
      {
        $lookup: {
          from: 'match',
          as: 'matches',
          localField: '_id',
          foreignField: isMenteeClass ? 'menteeID' : 'mentorID',
        },
      },
      ...(onlyUnmatched ? [{ $match: { matches: [] } }] : []),
      { $addFields: { numMatches: { $size: '$matches' } } },
      { $match: filter.getMongoFilter() },
      ...(numMatchesAspect
        ? [{ $match: { numMatches: { $gte: numMatchesAspect.min ?? 0, $lte: numMatchesAspect.max ?? Infinity } } }]
        : []),
      ...(skip ? [{ $skip: skip }] : []),
      ...(limit ? [{ $limit: limit }] : []),
      ...(isMenteeClass
        ? [
            {
              $lookup: {
                from: BuddyUser._collectionName,
                as: 'preferedMentors',
                localField: 'preferedMentorsIDs',
                foreignField: '_id',
              },
            },
          ]
        : []),
    ])
    .toArray();
};

const subscribeAllUnmatchedBuddiesInCurrentTimeframe = registerSubscription({
  operationName: 'subscribeAllUnmatchedBuddiesInCurrentTimeframe',
  middlewares: [
    checkAuthorizedMiddleware(({ user }) => user.hasPermission(MatchingPermission.CREATE_EDIT_DELETE_USERS)),
  ],
  resolve:
    (userClass: string, timeframeID: string, filter: AdornisFilter, skip?: number, limit?: number) =>
    <SelSet extends BaseQLSelectionSet>(gqlFields: SelSet) =>
      subscribeAllBuddyInCurrentTimeframeResolver(userClass, timeframeID, filter, true, skip, limit),
  type: () => [BuddyUser],
  params: [
    { name: 'userClass', type: () => String },
    { name: 'timeframeID', type: () => ID },
    { name: 'filter', type: () => AdornisFilter },
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
  ],
});

const subscribeAllBuddiesInCurrentTimeframe = registerSubscription({
  operationName: 'subscribeAllBuddiesInCurrentTimeframe',
  middlewares: [
    checkAuthorizedMiddleware(({ user }) => user.hasPermission(MatchingPermission.CREATE_EDIT_DELETE_USERS)),
  ],
  resolve:
    (userClass: string, timeframeID: string, filter: AdornisFilter, skip?: number, limit?: number) =>
    <SelSet extends BaseQLSelectionSet>(gqlFields: SelSet) =>
      subscribeAllBuddyInCurrentTimeframeResolver(userClass, timeframeID, filter, false, skip, limit),
  type: () => [BuddyUser],
  params: [
    { name: 'userClass', type: () => String },
    { name: 'timeframeID', type: () => ID },
    { name: 'filter', type: () => AdornisFilter },
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
  ],
});

const saveNoteOnBuddyUser = registerMutation({
  operationName: 'saveNoteOnBuddyUser',
  resolve: (userID: string, note: string) => async () => {
    const currUser = await getMyself<BuddyUser>()({ roles: selectionSet(() => AdornisRoleToContextsWrapper, 2) });
    if (!context.serverContext && !currUser?.hasPermission(MatchingPermission.CREATE_EDIT_DELETE_USERS))
      throw new Error('No permission to edit notes on users');

    const updateResult = await mongo()
      .collection(BuddyUser._collectionName)
      .updateOne({ _id: userID as any }, { $set: { note } });
    if (updateResult.modifiedCount === 1) return true;
    throw new Error(`Couldnt update note on user (userID: ${userID}; ${JSON.stringify(updateResult)})`);
  },
  type: () => Boolean,
  params: [
    { name: 'userID', type: () => ID },
    { name: 'note', type: () => String },
  ],
});

export const sendCustomMatchingMail = registerSubscription({
  operationName: 'sendCustomMatchingMail',
  params: [{ name: 'mails', type: () => [CustomMailRequest] }],
  type: () => String,
  resolve: (mailRequests: CustomMailRequest[]) => (): Observable<string> => {
    return from(getMyself<BuddyUser>()({ _id: 1, roles: selectionSet(() => AdornisRoleToContextsWrapper, 5) })).pipe(
      switchMap(currentUser => {
        if (!context.serverContext && !currentUser?.hasPermission(MatchingPermission.CREATE_EDIT_DELETE_USERS))
          throw new Error('No permission to send mails to users');

        const tasks = mailRequests.map((mailRequest, i) =>
          of({ id: `${i.toString()}/${mailRequests.length} E-Mails versandt`, mailRequest }),
        );
        return merge(...tasks);
      }),
      concatMap(async ({ id, mailRequest }) => {
        for (const email of mailRequest.mailAddresses) {
          await sendMail({
            subject: mailRequest.subject,
            content: mailRequest.content as any,
            email,
            cc: mailRequest.cc,
          });

          if (mailRequest.bcc) {
            for (const bcc of mailRequest.bcc)
              await sendMail({ subject: mailRequest.subject, content: mailRequest.content as any, email: bcc });
          }
        }

        await delay(100);

        return id;
      }),
    );
  },
});

async function sendMail(p: { email: string; cc?: Maybe<string> | string[]; content: string; subject: string }) {
  const { email, subject, content, cc } = p;

  const recipient = new Recipient(email, cc ?? undefined);
  const message = await prepareMessage({ subject: subject, html: content, attachments: [] });

  // make it possible to use this method with string html content
  if (typeof content === 'string') message.html = content;

  try {
    const _mailer = await mailer;
    if (!_mailer) throw new Error('could not get mailer');
    await _mailer.sendMail(message, recipient);
  } catch (err) {
    logger.error({ email, err }, 'E-Mail Konnte nicht verschickt werden; 8373');
  }
}

export const getAllUnsmatchedBuddiesInCurrentTimeframe = registerQuery({
  operationName: 'getAllUnsmatchedBuddiesInCurrentTimeframe',
  middlewares: [
    checkAuthorizedMiddleware(({ user }) => user.hasPermission(MatchingPermission.CREATE_EDIT_DELETE_USERS)),
  ],
  type: () => [BuddyUser],
  resolve: (userClass: string, timeframeID: string, filter: AdornisFilter, skip?: number, limit?: number) => {
    return (gqlFields: BaseQLSelectionSet<BuddyUser>) => {
      return getAllBuddiesInCurrentTimeframeResolver(userClass, timeframeID, filter, true, skip, limit);
    };
  },
  params: [
    { name: 'userClass', type: () => String },
    { name: 'timeframeID', type: () => ID },
    { name: 'filter', type: () => AdornisFilter },
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
  ],
});

export const getAllBuddiesInCurrentTimeframe = registerQuery({
  operationName: 'getAllBuddiesInCurrentTimeframe',
  middlewares: [
    checkAuthorizedMiddleware(({ user }) => user.hasPermission(MatchingPermission.CREATE_EDIT_DELETE_USERS)),
  ],
  type: () => [BuddyUser],
  resolve: (userClass: string, timeframeID: string, filter: AdornisFilter, skip?: number, limit?: number) => {
    return (gqlFields: BaseQLSelectionSet<BuddyUser>) => {
      return getAllBuddiesInCurrentTimeframeResolver(userClass, timeframeID, filter, false, skip, limit);
    };
  },
  params: [
    { name: 'userClass', type: () => String },
    { name: 'timeframeID', type: () => ID },
    { name: 'filter', type: () => AdornisFilter },
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
  ],
});

export const _getInCurrentTimeFrameCount = registerQuery({
  operationName: 'getInCurrentTimeframeCount',
  middlewares: [
    checkAuthorizedMiddleware(({ user }) => user.hasPermission(MatchingPermission.CREATE_EDIT_DELETE_USERS)),
  ],
  type: () => Int,
  resolve: (userClass: string, timeframeID: string, filter?: AdornisFilter) => {
    return async () => {
      return await getInTimeFrameCount(userClass, timeframeID, false, filter);
    };
  },
  params: [
    { name: 'userClass', type: () => String },
    { name: 'timeframeID', type: () => ID },
    { name: 'filter', type: () => AdornisFilter },
  ],
});

export const _getUnmatchedInCurrentTimeFrameCount = registerQuery({
  operationName: 'getUnmatchedInCurrentTimeframeCount',
  middlewares: [
    checkAuthorizedMiddleware(({ user }) => user.hasPermission(MatchingPermission.CREATE_EDIT_DELETE_USERS)),
  ],
  type: () => Int,
  resolve: (userClass: string, timeframeID: string, filter?: AdornisFilter) => {
    return async () => {
      return await getInTimeFrameCount(userClass, timeframeID, true, filter);
    };
  },
  params: [
    { name: 'userClass', type: () => String },
    { name: 'timeframeID', type: () => ID },
  ],
});

export namespace MatchingUserAPI {
  export const saveNote = saveNoteOnBuddyUser;
  export const subscribeAllInCurrentTimeframe = subscribeAllBuddiesInCurrentTimeframe;
  export const subscribeAllUnmatchedInCurrentTimeframe = subscribeAllUnmatchedBuddiesInCurrentTimeframe;
  export const getAllUnsmatchedInCurrentTimeframe = getAllUnsmatchedBuddiesInCurrentTimeframe;
  export const getAllInCurrentTimeframe = getAllBuddiesInCurrentTimeframe;
  export const getInCurrentTimeframeCount = _getInCurrentTimeFrameCount;
  export const getUnmatchedInCurrentTimeframeCount = _getUnmatchedInCurrentTimeFrameCount;
}

// const _onSendFreeFormMailToSingleUser = new Subject<{ userID: string; mailTemplate: MailTemplate }>();
// export const _onSendFreeFormMailToSingleUser = _onSendFreeFormMailToSingleUser.asObservable().pipe(share());

// export const sendFreeFormMailToSingleUser = registerQuery({
//   operationName: 'sendFreeFormMailToSingleUser',
//   type: () => Boolean,
//   params: [
//     { name: 'userID', type: () => ID },
//     { name: 'mailTemplate', type: () => MailTemplate },
//   ],
//   resolve: (userID: string, mailTemplate: MailTemplate) => {
//     return async () => {
//       _onSendFreeFormMailToSingleUser.next({ userID, mailTemplate });
//       return true;
//     };
//   },
// });
